import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Menu, X, ChevronRight } from 'lucide-react';

const NavBarBeta = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMouseEnter = (menu) => setActiveMenu(menu);
  const handleMouseLeave = () => setActiveMenu(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setActiveMenu(null);
  };

  // New function to close all menus
  const closeAll = () => {
    setActiveMenu(null);
    setMobileMenuOpen(false);
  };

  const Dropdown = ({ show, children, isMobile }) => (
    <div className={`
      ${isMobile ? '' : 'absolute top-full left-0'}
      bg-white rounded-xl shadow-lg py-3 min-w-[340px]
      transform transition-all duration-300 ease-in-out z-50
      ${!isMobile && 'border border-gray-100'}
      ${isMobile ? 'mt-2' : show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}
    `}>
      {children}
    </div>
  );

  const MenuItem = ({ to = '/', disabled = false, important = false, inProgress=false, children, onClick }) => {
    const Component = disabled ? 'div' : Link;
    const handleClick = (e) => {
      closeAll(); // Close all menus when item is clicked
      if (onClick) onClick(e);
    };

    return (
      <Component
        to={to}
        onClick={handleClick}
        className={`
          block px-4 py-2.5 text-sm transition-all duration-200
          hover:bg-gray-50 relative group
          ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'} 
          ${inProgress ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'} 
          ${important ? 'text-blue-600 font-medium hover:text-blue-700 bg-blue-50/50 hover:bg-blue-100/50' : 'hover:text-gray-900'}
        `}
      >
        <span className="relative z-10 flex items-center justify-between">
          <span className="flex items-center gap-2">
            {children}
          </span>
          {disabled && (
            <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded-full text-center font-medium">
              Coming Soon
            </span>
          )}
          {important && (
            <span className="ml-2 text-xs bg-blue-100/50 text-blue-600 px-2 py-1 rounded-full text-center font-medium">
              Available Now
            </span>
          )}
          {!disabled && inProgress && (
            <span className="ml-2 text-xs bg-yellow-100/50 text-yellow-600 px-2 py-1 rounded-full text-center font-medium">
              <span href={to} target="_blank" rel="noopener noreferrer">
                In Progress
              </span>
            </span>
          )}
          {!disabled && !important && (
            <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 -translate-x-2 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
          )}
        </span>
      </Component>
    );
  };

  const MenuSection = ({ title, children }) => (
    <div className="py-1">
      {title && (
        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </div>
      )}
      {children}
    </div>
  );

  const NavButton = ({ children, onClick, isActive }) => (
    <button 
      onClick={onClick}
      className="px-6 py-5 text-gray-700 hover:text-gray-900 font-medium transition-all duration-200 relative"
    >
      <span className="relative z-10">
        {children}
      </span>
      <span className={`
        absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 
        transform transition-all duration-300 ease-out
        ${isActive ? 'scale-x-100' : 'scale-x-0'}
      `} />
    </button>
  );

  return (
    <nav className="bg-white shadow-sm border-b border-gray-100 relative z-40">
      <div className="max-w-screen-2xl mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Home Button - Always Visible */}
          <Link 
            to="/insights/"
            onClick={closeAll}
            className="py-5 text-gray-700 hover:text-gray-900 transition-all duration-200 relative group flex items-center"
          >
            <div className="p-2 rounded-lg group-hover:bg-gray-50 transition-colors duration-200">
              <Home className="w-5 h-5" />
            </div>
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 transform transition-all duration-300 scale-x-0 group-hover:scale-x-100" />
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200"
            onClick={toggleMobileMenu}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-1 justify-center items-center gap-2">
            {/* Manager Selection */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('manager')}
              onMouseLeave={handleMouseLeave}
            >
              <NavButton isActive={activeMenu === 'manager'}>
                Manager Selection
              </NavButton>
              <Dropdown show={activeMenu === 'manager'}>
                <MenuSection title="Tools">
                  <MenuItem to="/insights/manager-selection/tools/return-attribution" important>
                    Return-Based Attribution Tool
                  </MenuItem>
                </MenuSection>
                <MenuSection title="Research">
                  <MenuItem to="/insights/manager-selection/research/quant-vs-fundamental">
                    Tracking Error Impact on Fundamental and Quantitative Active Managers
                  </MenuItem>
                </MenuSection>
              </Dropdown>
            </div>

            {/* Total Portfolio Management */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('portfolio')}
              onMouseLeave={handleMouseLeave}
            >
              <NavButton isActive={activeMenu === 'portfolio'}>
                Total Portfolio Management
              </NavButton>
              <Dropdown show={activeMenu === 'portfolio'}>
                <MenuSection title="Tools">
                  <MenuItem to="/insights/tpm/tools/pacing" important>Pacing Model</MenuItem>
                  <MenuItem to="/insights/tpm/tools/fit-to-ta" important>Fit Cashflows to Pacing Model</MenuItem>
                </MenuSection>
                <MenuSection title="Research">
                  <MenuItem to="/insights/tpm/research/market-efficiency">
                    Market Efficiency effect on Manager Performance
                  </MenuItem>
                  <MenuItem to="/insights/tpm/research/fundamental-quant-optimizer">
                    Public Equity: Quant vs. Fundamental Optimal Allocations
                  </MenuItem>
                </MenuSection>
              </Dropdown>
            </div>

            {/* Other */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('projects')}
              onMouseLeave={handleMouseLeave}
            >
              <NavButton isActive={activeMenu === 'projects'}>
                Other
              </NavButton>
              <Dropdown show={activeMenu === 'projects'}>
                <MenuSection title="Projects">
                  <MenuItem to="/insights/other/projects/penny-ml">
                    Micro-Cap Quantitative Fund
                  </MenuItem>
                </MenuSection>
                <MenuSection title="Research">
                  <MenuItem to="/insights/other/research/evestment-bias">
                    Bias in the eVestment Dataset
                  </MenuItem>
                </MenuSection>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`
          md:hidden
          ${mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}
          overflow-hidden transition-all duration-300 ease-in-out
        `}>
          <div className="space-y-2 py-3">
            {/* Manager Selection Mobile */}
            <div>
              <NavButton 
                onClick={() => setActiveMenu(activeMenu === 'manager' ? null : 'manager')}
                isActive={activeMenu === 'manager'}
              >
                Manager Selection
              </NavButton>
              {activeMenu === 'manager' && (
                <Dropdown isMobile>
                  <MenuSection title="Tools">
                    <MenuItem 
                      to="/insights/manager-selection/tools/return-attribution" 
                      important
                    >
                      Return-Based Attribution Tool
                    </MenuItem>
                  </MenuSection>
                  <MenuSection title="Research">
                    <MenuItem 
                      to="/insights/manager-selection/research/quant-vs-fundamental"
                    >
                      Tracking Error Impact on Fundamental and Quantitative Active Managers
                    </MenuItem>
                  </MenuSection>
                </Dropdown>
              )}
            </div>

            {/* Total Portfolio Management Mobile */}
            <div>
              <NavButton 
                onClick={() => setActiveMenu(activeMenu === 'portfolio' ? null : 'portfolio')}
                isActive={activeMenu === 'portfolio'}
              >
                Total Portfolio Management
              </NavButton>
              {activeMenu === 'portfolio' && (
                <Dropdown isMobile>
                  <MenuSection title="Tools">
                    <MenuItem to="/insights/tpm/tools/pacing" important>
                      Pacing Model
                    </MenuItem>
                    <MenuItem to="/insights/tpm/tools/fit-to-ta" important>
                      Fit Cashflows to Pacing Model
                    </MenuItem>
                  </MenuSection>
                  <MenuSection title="Research">
                    <MenuItem 
                      to="/insights/tpm/research/market-efficiency"
                    >
                      Market Efficiency effect on Manager Performance
                    </MenuItem>
                    <MenuItem>
                      Public Equity: Quant vs. Fundamental Optimal Allocations
                    </MenuItem>
                  </MenuSection>
                </Dropdown>
              )}
            </div>

            {/* Other Mobile */}
            <div>
              <NavButton 
                onClick={() => setActiveMenu(activeMenu === 'projects' ? null : 'projects')}
                isActive={activeMenu === 'projects'}
              >
                Other
              </NavButton>
              {activeMenu === 'projects' && (
                <Dropdown isMobile>
                  <MenuSection title="Projects">
                    <MenuItem 
                      to="/insights/other/projects/penny-ml"
                    >
                      Micro-Cap Quantitative Fund
                    </MenuItem>
                  </MenuSection>
                  <MenuSection title="Research">
                    <MenuItem 
                      to="/insights/other/research/evestment-bias"
                    >
                      Bias in the eVestment Dataset
                    </MenuItem>
                  </MenuSection>
                </Dropdown>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBarBeta;
